export const DAMAGE_UPLOAD_DB_NAME = "autoeinfachlos-damage-uploads";
export const DAMAGE_UPLOAD_DB_VERSION = 1;
export const DAMAGE_UPLOAD_STORE_NAME = "damage-upload-records";

const OWNER_INDEX = "ownerEmail";
const DEFAULT_STORE_OPERATION_TIMEOUT_MS = 5000;
const TERMINAL_STATES = new Set([
  "deleting",
  "confirmed",
  "deleted",
  "superseded",
]);
const STATE_RANK = {
  pending: 0,
  failed: 1,
  committed: 2,
  superseded: 3,
  deleted: 4,
};

let databasePromise = null;
let openDatabase = null;
let storeOperationTimeoutMs = DEFAULT_STORE_OPERATION_TIMEOUT_MS;

function createStorageTimeoutError(operation) {
  const error = new Error(
    `IndexedDB für Schadenfotos reagiert nicht (${operation}).`
  );
  error.name = "DamageUploadStorageTimeoutError";
  return error;
}

function assertRecord(record) {
  if (!record || typeof record !== "object" || !record.key) {
    throw new TypeError("Ein Schadenfoto-Upload benötigt einen gültigen Record.");
  }
}

function openDamageUploadDatabase() {
  if (!globalThis?.indexedDB) {
    throw new Error("IndexedDB ist in dieser Umgebung nicht verfügbar.");
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const request = globalThis.indexedDB.open(
      DAMAGE_UPLOAD_DB_NAME,
      DAMAGE_UPLOAD_DB_VERSION
    );
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      databasePromise = null;
      reject(createStorageTimeoutError("open"));
    }, storeOperationTimeoutMs);

    const fail = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      databasePromise = null;
      reject(error);
    };

    request.onupgradeneeded = () => {
      if (settled) {
        try {
          request.transaction?.abort();
        } catch {}
        return;
      }
      const database = request.result;
      const store = database.objectStoreNames.contains(DAMAGE_UPLOAD_STORE_NAME)
        ? request.transaction.objectStore(DAMAGE_UPLOAD_STORE_NAME)
        : database.createObjectStore(DAMAGE_UPLOAD_STORE_NAME, {
            keyPath: "key",
          });
      if (!store.indexNames.contains(OWNER_INDEX)) {
        store.createIndex(OWNER_INDEX, "ownerEmail", { unique: false });
      }
    };
    request.onerror = () =>
      fail(
        request.error ||
          new Error("IndexedDB für Schadenfotos konnte nicht geöffnet werden.")
      );
    request.onblocked = () =>
      fail(new Error("IndexedDB für Schadenfotos ist blockiert."));
    request.onsuccess = () => {
      const database = request.result;
      if (settled) {
        database.close();
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      openDatabase = database;
      database.onversionchange = () => {
        database.close();
        if (openDatabase === database) openDatabase = null;
        databasePromise = null;
      };
      resolve(database);
    };
  });
}

function getDatabase() {
  if (!databasePromise) databasePromise = openDamageUploadDatabase();
  return databasePromise;
}

async function runTransaction(mode, work) {
  const database = await getDatabase();
  return new Promise((resolve, reject) => {
    let result;
    let settled = false;
    const transaction = database.transaction(DAMAGE_UPLOAD_STORE_NAME, mode);
    const store = transaction.objectStore(DAMAGE_UPLOAD_STORE_NAME);
    const timeoutId = setTimeout(() => {
      fail(createStorageTimeoutError("transaction"));
      database.close();
      if (openDatabase === database) openDatabase = null;
      databasePromise = null;
    }, storeOperationTimeoutMs);
    const fail = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      try {
        transaction.abort();
      } catch {}
      reject(error || new Error("IndexedDB-Transaktion fehlgeschlagen."));
    };

    transaction.oncomplete = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(result);
    };
    transaction.onerror = () => fail(transaction.error);
    transaction.onabort = () => fail(transaction.error);

    try {
      work(
        store,
        (value) => {
          result = value;
        },
        fail
      );
    } catch (error) {
      fail(error);
    }
  });
}

function isIncomingCurrent(existing, incoming) {
  if (!existing) return true;
  if (existing.uploadId === incoming.uploadId) {
    if (existing.state === "deleting" && incoming.state === "confirmed") {
      return false;
    }
    return (
      !TERMINAL_STATES.has(existing.state) ||
      TERMINAL_STATES.has(incoming.state)
    );
  }

  const existingCapturedAt = Number(existing.capturedAt || 0);
  const incomingCapturedAt = Number(incoming.capturedAt || 0);
  if (existingCapturedAt !== incomingCapturedAt) {
    return incomingCapturedAt > existingCapturedAt;
  }
  return String(incoming.uploadId || "") > String(existing.uploadId || "");
}

export async function putDamageUploadRecordIfCurrent(record) {
  assertRecord(record);
  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(record.key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      if (!isIncomingCurrent(getRequest.result, record)) {
        setResult(false);
        return;
      }
      const putRequest = store.put(record);
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult(true);
    };
  });
}

export async function updateDamageUploadRecord(key, uploadId, patch) {
  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (
        !current ||
        current.uploadId !== uploadId ||
        TERMINAL_STATES.has(current.state)
      ) {
        setResult(false);
        return;
      }
      const currentRank = STATE_RANK[current.state] ?? 0;
      const nextRank = STATE_RANK[patch?.state] ?? currentRank;
      if (nextRank < currentRank) {
        setResult(false);
        return;
      }
      const putRequest = store.put({ ...current, ...patch, key, uploadId });
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult(true);
    };
  });
}

export async function getDamageUploadRecord(key) {
  return runTransaction("readonly", (store, setResult, fail) => {
    const request = store.get(key);
    request.onerror = () => fail(request.error);
    request.onsuccess = () => setResult(request.result || null);
  });
}

export async function getDamageUploadRecordsByOwner(ownerEmail) {
  if (!ownerEmail) return [];
  return runTransaction("readonly", (store, setResult, fail) => {
    const request = store.index(OWNER_INDEX).getAll(ownerEmail);
    request.onerror = () => fail(request.error);
    request.onsuccess = () => setResult(request.result || []);
  });
}

export async function markDamageUploadDeleting(key, uploadId) {
  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (
        !current ||
        current.uploadId !== uploadId ||
        ["confirmed", "deleted", "superseded"].includes(current.state)
      ) {
        setResult({ ok: false, current: current || null });
        return;
      }
      const next = {
        ...current,
        stateBeforeDelete:
          current.state === "deleting"
            ? current.stateBeforeDelete
            : current.state,
        phaseBeforeDelete:
          current.state === "deleting"
            ? current.phaseBeforeDelete
            : current.phase || null,
        state: "deleting",
        phase: null,
        updatedAt: Date.now(),
      };
      const putRequest = store.put(next);
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult({ ok: true, record: next });
    };
  });
}

export async function restoreDamageUploadAfterDeleteFailure(key, uploadId) {
  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (
        !current ||
        current.uploadId !== uploadId ||
        current.state !== "deleting"
      ) {
        setResult({ ok: false, current: current || null });
        return;
      }
      const next = { ...current };
      next.state =
        current.stateBeforeDelete ||
        (current.imageUrl ? "committed" : "pending");
      next.phase =
        next.state === "pending"
          ? current.phaseBeforeDelete || "queued"
          : null;
      delete next.stateBeforeDelete;
      delete next.phaseBeforeDelete;
      next.updatedAt = Date.now();
      const putRequest = store.put(next);
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult({ ok: true, record: next });
    };
  });
}

export async function tombstoneDamageUploadRecord(record) {
  assertRecord(record);
  return runTransaction("readwrite", (store, setResult, fail) => {
    const getRequest = store.get(record.key);
    getRequest.onerror = () => fail(getRequest.error);
    getRequest.onsuccess = () => {
      const current = getRequest.result;
      if (current && !isIncomingCurrent(current, record)) {
        setResult(false);
        return;
      }
      const next = {
        ...(current || {}),
        ...record,
        blob: null,
        locationBlob: null,
        state: "deleted",
        updatedAt: Date.now(),
      };
      delete next.stateBeforeDelete;
      delete next.phaseBeforeDelete;
      const putRequest = store.put(next);
      putRequest.onerror = () => fail(putRequest.error);
      putRequest.onsuccess = () => setResult(true);
    };
  });
}

export async function clearDamageUploadRecordsForTests() {
  return runTransaction("readwrite", (store, setResult, fail) => {
    const request = store.clear();
    request.onerror = () => fail(request.error);
    request.onsuccess = () => setResult(true);
  });
}

export function closeDamageUploadStoreForTests() {
  openDatabase?.close();
  openDatabase = null;
  databasePromise = null;
}

export function __setDamageUploadStoreTimeoutForTests(timeoutMs = null) {
  storeOperationTimeoutMs =
    timeoutMs == null
      ? DEFAULT_STORE_OPERATION_TIMEOUT_MS
      : Math.max(1, Number(timeoutMs) || 1);
}

export function __isIncomingDamageUploadRecordCurrentForTests(
  existing,
  incoming
) {
  return isIncomingCurrent(existing, incoming);
}
